import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StorageHelperService } from '../../shared/services/storage.helper';
import { IAuthTokenModel } from '../models/interfaces/auth.interface';
import { AuthDataService } from '../services/data-services/auth-data.service';

const excludedRequest = ['auth', 'auth/login', '/auth/refresh-token', '/auth/users'];

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const tokenInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const storage = inject(StorageHelperService);
  const store = inject(Store);
  const authDataService = inject(AuthDataService);
  const platformId = inject(PLATFORM_ID);

  let request = req;
  if (storage.token && !excludedRequest.some(e => req.url.includes(e))) {
    request = req.clone({
      setHeaders: {
        Authorization: `Bearer ${storage.token}`,
      },
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        !error.url?.includes('auth') &&
        !error.url?.includes('auth/login') &&
        !error.url?.includes('auth/refresh-token') &&
        !error.url?.includes('/auth/users')
      ) {
        if (!storage.checkExistRefreshToken || !storage.checkExistToken) {
          reloadState(platformId);
          storage.removeToken();
          return throwError(() => error);
        } else {
          return handle401Error(request, next, storage, store, authDataService, platformId);
        }
      } else {
        return throwError(() => error);
      }
    }),
  );
};

function reloadState(platformId: any): void {
  if (isPlatformBrowser(platformId)) {
    window.location.reload();
  }
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  storage: StorageHelperService,
  store: Store,
  authDataService: AuthDataService,
  platformId: Object,
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authDataService.updateRefreshToken().pipe(
      switchMap((data: IAuthTokenModel | HttpErrorResponse) => {
        isRefreshing = false;

        if (data instanceof HttpErrorResponse) {
          storage.removeToken();
          reloadState(platformId);
          return throwError(() => data);
        }

        storage.setData(data);
        refreshTokenSubject.next(data.accessToken);

        const clonedRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${data.accessToken}`,
          },
        });
        return next(clonedRequest);
      }),
      catchError(err => {
        isRefreshing = false;
        storage.removeToken();
        reloadState(platformId);
        return throwError(() => err);
      }),
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => !!token),
      take(1),
      switchMap(token => {
        const clonedRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
        return next(clonedRequest);
      }),
    );
  }
}
